import { FilterInput, Flex, PrimaryButton } from '@wrisk/ui-components'
import { range } from 'lodash-es'
import { DateTime } from 'luxon'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { VehicleKBA } from '../../../../../domain'
import {
  TExtends,
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { VehicleMakesFilter } from './VehicleMakesFilter'
import { VehicleModelsFilter } from './VehicleModelsFilter'
import { VehicleResultsFilter } from './VehicleResultsFilter'

interface VehicleSelectConfig {
  profile?: string
  earliestYear?: number
}

export interface VehicleSelectKBAProps {
  config?: VehicleSelectConfig
  value: VehicleKBA | null
  onChange: (value: VehicleKBA | null) => void
  tKey: TKeyBuilder
}

const isVehicle = (data: Partial<VehicleKBA>): data is VehicleKBA =>
  Boolean(data.vehicleCode)

export const VehicleSelectKBA: FunctionComponent<VehicleSelectKBAProps> = ({
  config,
  value,
  onChange,
  tKey,
}) => {
  const { t } = useWriskTranslation()

  const [data, setData] = useState<VehicleKBA>(value ?? {})

  const years = useMemo(
    () => range(DateTime.local().get('year'), (config?.earliestYear ?? 1940) - 1),
    [config?.earliestYear],
  )

  const onInternalConfirm = useCallback(() => {
    if (isVehicle(data)) {
      onChange(data)
    }
  }, [data, onChange])

  const onResultSelect = useCallback((selection: VehicleKBA | null) => {
    setData((prevState) => selection ?? prevState)
  }, [])

  const setDataAndResetSelection = useCallback(
    (selection: Partial<VehicleKBA>) => {
      setData(selection)
      onChange(null)
    },
    [onChange],
  )

  const setYear = useCallback(
    (year: number | undefined) => {
      setDataAndResetSelection({
        year,
      })
    },
    [setDataAndResetSelection],
  )

  const setMake = useCallback(
    (make: string | undefined) => {
      setDataAndResetSelection({
        year: data.year,
        make,
      })
    },
    [data, setDataAndResetSelection],
  )

  const setModel = useCallback(
    (model: string | undefined) => {
      setDataAndResetSelection({
        year: data.year,
        make: data.make,
        model,
      })
    },
    [data, setDataAndResetSelection],
  )

  const makesDropdown = data.year && (
    <VehicleMakesFilter
      onSelect={setMake}
      profile={config?.profile}
      year={data.year}
      make={data.make}
      tKey={TExtends(tKey, 'make')}
    />
  )

  const modelsDropdown = data.year && data.make && (
    <VehicleModelsFilter
      onSelect={setModel}
      profile={config?.profile}
      year={data.year}
      make={data.make}
      model={data.model}
      tKey={TExtends(tKey, 'model')}
    />
  )

  const vehiclesResults = data.year && data.make && data.model && (
    <VehicleResultsFilter
      onChange={onResultSelect}
      profile={config?.profile}
      year={data.year}
      make={data.make}
      model={data.model}
      value={data}
      tKey={TExtends(tKey, 'results')}
    />
  )

  const disabled = !isVehicle(data)

  return (
    <Flex
      justifyContent='flex-start'
      flexDirection='column'
      alignItems='flex-start'
      flexGrow={1}
      width={1}
      gap={3}
    >
      <FilterInput
        data-testid='vehicle-year'
        width={1}
        placeholder={t<string>(tKey('year', 'placeholder'))}
        value={data.year}
        onSelect={setYear}
        options={years}
        renderOption={renderYear}
        renderOptionText={renderYear}
        matchOption={matchYear}
      />

      {makesDropdown}
      {modelsDropdown}
      {vehiclesResults}

      <PrimaryButton
        data-testid='vehicle-confirm'
        disabled={disabled}
        onClick={onInternalConfirm}
      >
        {t(tKey('actions', 'confirm'))}
      </PrimaryButton>
    </Flex>
  )
}

const renderYear = (year: number) => year.toString()
const matchYear = (text: string) => (yr: number) => yr.toString().includes(text)
